/**
 * useAdminNewsletters — Admin-Newsletter mit Segment-Filtern (heliumtracker-web#96)
 *
 * GET   /api/v2/admin/newsletters
 * GET   /api/v2/admin/newsletters/:id
 * POST  /api/v2/admin/newsletters/preview_count
 * POST  /api/v2/admin/newsletters
 * PATCH /api/v2/admin/newsletters/:id
 * GET   /api/v2/admin/newsletters/:id/preview
 * POST  /api/v2/admin/newsletters/:id/test_send
 * POST  /api/v2/admin/newsletters/:id/publish
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@heliumtracker/api-types'
import { useAuthStore } from '../../stores/authStore'
import i18n from '../../i18n'

export const NEWSLETTER_SEGMENTS = [
  'paid_premium',
  'trial_only',
  'active_hotspot',
  'any_hotspot',
  'iot_hotspot',
  'mobile_hotspot',
  'no_hotspot',
  'legacy_account',
] as const

export type NewsletterSegment = (typeof NEWSLETTER_SEGMENTS)[number]

export type AdminNewsletter = components['schemas']['AdminNewsletter']
export type AdminNewsletterDetail = components['schemas']['AdminNewsletterDetail']

export type NewsletterFilterParams = {
  segments: string[]
  exclude_newsletter_id?: number | null
  max_recipients?: number | null
}

function getAuthToken(): string | null {
  return useAuthStore.getState().accessToken ?? null
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken()
  return token ? { Authorization: 'Bearer ' + token } : {}
}

function apiUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  return baseUrl + path
}

async function fetchAdminNewsletters(): Promise<AdminNewsletter[]> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters'), { headers: authHeaders() })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.loadFailed', { status: response.status }))
  }
  const json = (await response.json()) as { newsletters: AdminNewsletter[] }
  return json.newsletters
}

export function useAdminNewsletters() {
  const query = useQuery<AdminNewsletter[]>({
    queryKey: ['admin-newsletters'],
    queryFn: fetchAdminNewsletters,
    retry: false,
    staleTime: 10000,
  })
  return {
    newsletters: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export type PreviewCountResult = { count: number; capped_count: number }

async function fetchPreviewCount(params: NewsletterFilterParams): Promise<PreviewCountResult> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/preview_count'), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.previewFailed'))
  }
  return response.json()
}

export function useNewsletterPreviewCount(params: NewsletterFilterParams) {
  const enabled = params.segments.length > 0
  const query = useQuery<PreviewCountResult>({
    queryKey: ['admin-newsletter-preview-count', params],
    queryFn: () => fetchPreviewCount(params),
    enabled,
    retry: false,
  })
  return {
    count: query.data?.count,
    cappedCount: query.data?.capped_count,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
  }
}

export type CreateNewsletterInput = {
  subject: string
  body_html: string
  segments: string[]
  exclude_newsletter_id?: number | null
  max_recipients?: number | null
}

async function createNewsletter(input: CreateNewsletterInput): Promise<AdminNewsletter> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters'), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.createFailed'))
  }
  const json = (await response.json()) as { newsletter: AdminNewsletter }
  return json.newsletter
}

export function useCreateNewsletter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createNewsletter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletters'] })
    },
  })
}

async function fetchAdminNewsletter(id: number): Promise<AdminNewsletterDetail> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/' + id), { headers: authHeaders() })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.loadFailed', { status: response.status }))
  }
  const json = (await response.json()) as { newsletter: AdminNewsletterDetail }
  return json.newsletter
}

/** Detail inkl. body_html — nur laden, wenn wirklich ein Newsletter bearbeitet wird (Body ~20 KB). */
export function useAdminNewsletter(id: number | null) {
  const query = useQuery<AdminNewsletterDetail>({
    queryKey: ['admin-newsletter', id],
    queryFn: () => fetchAdminNewsletter(id as number),
    enabled: id !== null,
    retry: false,
  })
  return {
    newsletter: query.data,
    isLoading: id !== null && query.isLoading,
    isError: query.isError,
  }
}

export type UpdateNewsletterInput = CreateNewsletterInput & { id: number }

/** Wirft bei 409 (bereits versendet) einen Error mit `.status = 409`. */
async function updateNewsletter({ id, ...input }: UpdateNewsletterInput): Promise<AdminNewsletterDetail> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/' + id), {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    const err = new Error(
      response.status === 409
        ? i18n.t('adminNewsletters:toast.updateAlreadySent')
        : i18n.t('adminNewsletters:toast.updateFailed'),
    ) as Error & { status?: number }
    err.status = response.status
    throw err
  }
  const json = (await response.json()) as { newsletter: AdminNewsletterDetail }
  return json.newsletter
}

export function useUpdateNewsletter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateNewsletter,
    onSuccess: (newsletter) => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletters'] })
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter', newsletter.id] })
      // Die gerenderte Vorschau haengt am Body — nach dem Speichern neu holen.
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-preview', newsletter.id] })
    },
  })
}

async function fetchNewsletterPreview(id: number): Promise<string> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/' + id + '/preview'), { headers: authHeaders() })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.previewHtmlFailed'))
  }
  const json = (await response.json()) as { html: string }
  return json.html
}

/**
 * Fertiges Mail-HTML fuer die Vorschau. Kommt als String (nicht als iframe-URL),
 * weil der Endpunkt Bearer-Auth verlangt — die Page rendert es via srcDoc.
 */
export function useNewsletterPreview(id: number | null) {
  const query = useQuery<string>({
    queryKey: ['admin-newsletter-preview', id],
    queryFn: () => fetchNewsletterPreview(id as number),
    enabled: id !== null,
    retry: false,
  })
  return {
    html: query.data,
    isLoading: id !== null && query.isLoading,
    isError: query.isError,
  }
}

async function testSendNewsletter(id: number): Promise<{ sent_to: string }> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/' + id + '/test_send'), {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    throw new Error(i18n.t('adminNewsletters:toast.testSendFailed'))
  }
  return response.json()
}

export function useTestSendNewsletter() {
  return useMutation({ mutationFn: testSendNewsletter })
}

export type PublishNewsletterResult = { newsletter: AdminNewsletter; recipient_count: number }

/** Wirft bei 409 (bereits versendet) einen Error mit `.status = 409`, damit die Page das gesondert anzeigen kann. */
async function publishNewsletter(id: number): Promise<PublishNewsletterResult> {
  const response = await fetch(apiUrl('/api/v2/admin/newsletters/' + id + '/publish'), {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = new Error(
      response.status === 409
        ? i18n.t('adminNewsletters:toast.publishAlreadySent')
        : i18n.t('adminNewsletters:toast.publishFailed'),
    ) as Error & { status?: number }
    err.status = response.status
    throw err
  }
  return response.json()
}

export function usePublishNewsletter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: publishNewsletter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletters'] })
    },
  })
}
